import {useMemo} from 'react';

import {OpportunityBox} from 'modules/growth/sections/BoxSidebar/types';
import {Falsy} from 'types';

import {
  DECISIONS_ACCOUNT,
  DECISIONS_ACCOUNT_EXECUTIVE,
  DECISIONS_ACCOUNT_EXECUTIVES,
  DECISIONS_ACCOUNT_MANAGER,
  DECISIONS_ACCOUNT_MANAGERS,
  DECISIONS_ACCOUNTS,
  DECISIONS_ACTIVITIES,
  DECISIONS_ACTIVITY_URL,
  DECISIONS_CARRIER_APPETITE,
  DECISIONS_EMPLOYEE_ACTIVITY_URL,
  DECISIONS_INDUSTRIES,
  DECISIONS_INDUSTRY,
  DECISIONS_INDUSTRY_GROUP,
  DECISIONS_INDUSTRY_SUBGROUP,
  DECISIONS_KPI,
  DECISIONS_MARKET_URL_BASE,
  DECISIONS_MARKETS,
  DECISIONS_MONOLINE_RATIOS,
  DECISIONS_MONOLINE_RATIOS_ENTITY_URL,
  DECISIONS_O360SCORE,
  DECISIONS_O360SCORE_PRODUCER_URL,
  DECISIONS_PAGES_RENEWALS,
  DECISIONS_PRODUCER,
  DECISIONS_PRODUCERS,
  DECISIONS_PRODUCT,
  DECISIONS_PRODUCT_GROUP,
  DECISIONS_PRODUCT_INSURANCE_SEGMENT,
  DECISIONS_PRODUCT_SUPER_GROUP,
  DECISIONS_PRODUCTS,
  DECISIONS_REPORTS,
  DECISIONS_RETENTION,
  DECISIONS_WHATS_NEW,
  ENNABL_CHAT_AI,
  ENNABL_DECISIONS,
  ENNABL_GROWTH,
  ENNABL_MGA_DASHBOARD,
  ENNABL_WORKFLOWS,
  GROWTH_CARRIER_APPETITE,
  GROWTH_GOALS,
  GROWTH_LISTS,
  GROWTH_OPPORTUNITIES,
  GROWTH_REMINDERS,
  MarketType,
  MGA_DASHBOARD_MARKET,
  MGA_DASHBOARD_MARKETS,
  MGA_DASHBOARD_PRODUCER,
  MGA_DASHBOARD_PRODUCERS,
  MGA_DASHBOARD_REPORTS,
  MGA_DASHBOARD_RETAIL_CLIENT,
  MGA_DASHBOARD_RETAIL_CLIENTS,
} from 'core/constants/paths';
import {useIsProducer, useRestrictedAccessPermissions} from 'core/hooks/authHooks';
import {useProducerViewWithCarrierAppetiteFlag} from 'utils/hooks/useProducerViewWithCarrierAppetiteFlag';
import {useWhatsNewFiltersStore} from 'modules/decisionsNext/sections/WhatsNewSection';
import {RouteEntityId} from 'modules/decisionsNext/constants/routeEntityId';
import {useProjectName} from 'utils/hooks/useProjectName';
import {useCheckEntityAccess} from 'core/hooks/useEntityAccess';

import {IconKey, SidebarSection, SectionItem} from './types';

type LoosySidebarSection = Omit<SidebarSection, 'items'> & {
  items: Falsy<SectionItem>[];
};

export const useSidebarSections = () => {
  const {isMga} = useProjectName();
  const checkAccess = useCheckEntityAccess();
  const isProducer = useIsProducer();
  const {isProducerWithCarrierAppetite} = useProducerViewWithCarrierAppetiteFlag();
  const {newFeaturesCount, hasAlreadySeenWhatsNewPage} = useWhatsNewFiltersStore(store => ({
    newFeaturesCount: store.newFeaturesCount,
    hasAlreadySeenWhatsNewPage: store.hasAlreadySeenWhatsNewPage,
  }));
  const {onlyDocumentsPermission} = useRestrictedAccessPermissions();

  const home = useMemo(() => {
    if (isProducer) {
      return null;
    }

    return {
      icon: IconKey.Home,
      title: 'Home',
      to: isMga ? ENNABL_MGA_DASHBOARD : ENNABL_DECISIONS,
      matchPattern: [
        {
          matchPattern: isMga ? ENNABL_MGA_DASHBOARD : ENNABL_DECISIONS,
          params: {end: true},
        },
        {
          matchPattern: `${ENNABL_DECISIONS}/home/:${RouteEntityId.dashboard}/:${RouteEntityId.section}`,
        },
      ],
    };
  }, [isProducer, isMga]);

  const sections = useMemo(() => {
    if (isMga) {
      return [
        {
          title: 'Wholesale',
          items: [
            {
              icon: IconKey.RetailClients,
              title: 'Agencies',
              to: MGA_DASHBOARD_RETAIL_CLIENTS,
              matchPattern: [{matchPattern: MGA_DASHBOARD_RETAIL_CLIENTS}, {matchPattern: MGA_DASHBOARD_RETAIL_CLIENT}],
            },
            {
              icon: IconKey.Markets,
              title: 'Markets',
              to: MGA_DASHBOARD_MARKETS,
              matchPattern: [{matchPattern: MGA_DASHBOARD_MARKETS}, {matchPattern: MGA_DASHBOARD_MARKET}],
            },
            {
              icon: IconKey.Producers,
              title: 'UW/Brokers',
              to: MGA_DASHBOARD_PRODUCERS,
              matchPattern: [{matchPattern: MGA_DASHBOARD_PRODUCERS}, {matchPattern: MGA_DASHBOARD_PRODUCER}],
            },
            {
              icon: IconKey.Reports,
              title: 'Report Studio',
              to: MGA_DASHBOARD_REPORTS,
              matchPattern: [{matchPattern: MGA_DASHBOARD_REPORTS}],
            },
          ],
        },
      ] satisfies LoosySidebarSection[];
    }

    if (onlyDocumentsPermission) {
      return [
        {
          title: 'DISCOVER',
          items: [
            checkAccess('withWorkflows') && {
              icon: IconKey.Documents,
              title: 'Workflows',
              to: ENNABL_WORKFLOWS,
              new: true,
              matchPattern: [{matchPattern: ENNABL_WORKFLOWS}],
            },
          ],
        },
      ] satisfies LoosySidebarSection[];
    }

    if (isProducer) {
      return [
        {
          title: undefined,
          items: [
            {
              icon: IconKey.Opportunities,
              title: 'Opportunities',
              to: `${GROWTH_OPPORTUNITIES}/${OpportunityBox.NetNew}`,
              matchPattern: [
                {
                  matchPattern: GROWTH_OPPORTUNITIES,
                },
                {
                  matchPattern: GROWTH_LISTS,
                },
                {
                  matchPattern: GROWTH_REMINDERS,
                },
              ],
            },
            isProducerWithCarrierAppetite
              ? {
                  icon: IconKey.CarrierAppetite,
                  title: 'Carrier Appetite',
                  to: GROWTH_CARRIER_APPETITE,
                  matchPattern: [
                    {
                      matchPattern: GROWTH_CARRIER_APPETITE,
                    },
                  ],
                }
              : checkAccess('withInsights') && {
                  icon: IconKey.Insights,
                  title: 'Insights',
                  to: ENNABL_DECISIONS,
                  matchPattern: [
                    {
                      matchPattern: ENNABL_DECISIONS,
                    },
                  ],
                },
            checkAccess('withMyGoals') && {
              icon: IconKey.AccountSettings,
              title: 'My Goals',
              to: GROWTH_GOALS,
              matchPattern: [
                {
                  matchPattern: GROWTH_GOALS,
                },
              ],
            },
            checkAccess('withWorkflows') && {
              icon: IconKey.Documents,
              title: 'Workflows',
              to: ENNABL_WORKFLOWS,
              new: true,
              matchPattern: [{matchPattern: ENNABL_WORKFLOWS}],
            },
          ],
        },
      ] satisfies LoosySidebarSection[];
    }

    return [
      {
        title: 'DISCOVER',
        items: [
          checkAccess('withWhatsNew') && {
            icon: IconKey.WhatsNew,
            title: "What's new",
            to: DECISIONS_WHATS_NEW,
            matchPattern: [{matchPattern: DECISIONS_WHATS_NEW}],
            badgeContent: newFeaturesCount > 0 ? String(newFeaturesCount) : undefined,
            new: !hasAlreadySeenWhatsNewPage,
          },
          checkAccess('withEnnablAI') && {
            icon: IconKey.AI,
            title: 'ennablAI',
            to: ENNABL_CHAT_AI,
            matchPattern: [{matchPattern: ENNABL_CHAT_AI}],
          },
          checkAccess('withGrowth') && {
            icon: IconKey.Growth,
            title: 'Growth',
            to: ENNABL_GROWTH,
            matchPattern: [{matchPattern: ENNABL_GROWTH}],
          },
          checkAccess('withKpis') && {
            icon: IconKey.KPI,
            title: 'KPIs',
            to: DECISIONS_KPI,
            matchPattern: [{matchPattern: DECISIONS_KPI}],
          },
          checkAccess('withWorkflows') && {
            icon: IconKey.Documents,
            title: 'Workflows',
            to: ENNABL_WORKFLOWS,
            new: true,
            matchPattern: [{matchPattern: ENNABL_WORKFLOWS}],
          },
          checkAccess('withO360Score') && {
            icon: IconKey.O360Score,
            title: 'O360 Score',
            to: DECISIONS_O360SCORE,
            matchPattern: [{matchPattern: DECISIONS_O360SCORE}, {matchPattern: DECISIONS_O360SCORE_PRODUCER_URL}],
          },
        ],
      },
      {
        title: 'BOOK',
        items: [
          checkAccess('withAccounts') && {
            icon: IconKey.Accounts,
            title: 'Accounts',
            to: DECISIONS_ACCOUNTS,
            matchPattern: [{matchPattern: DECISIONS_ACCOUNTS}, {matchPattern: DECISIONS_ACCOUNT}],
          },
          checkAccess('withRenewals') && {
            icon: IconKey.Renewals,
            title: 'Renewals',
            to: DECISIONS_PAGES_RENEWALS,
            matchPattern: [{matchPattern: DECISIONS_PAGES_RENEWALS}],
          },
          checkAccess('withReportsStudio') && {
            icon: IconKey.Reports,
            title: 'Report Studio',
            to: DECISIONS_REPORTS,
            matchPattern: [{matchPattern: DECISIONS_REPORTS}],
          },
          checkAccess('withRetention') && {
            icon: IconKey.Retention,
            title: 'Retention',
            to: DECISIONS_RETENTION,
            new: true,
            matchPattern: [{matchPattern: DECISIONS_RETENTION}],
          },
          checkAccess('withMonolineRatios') && {
            icon: IconKey.MonolineRatios,
            title: 'Monoline Ratios',
            new: true,
            to: DECISIONS_MONOLINE_RATIOS,
            matchPattern: [
              {matchPattern: DECISIONS_MONOLINE_RATIOS},
              {matchPattern: DECISIONS_MONOLINE_RATIOS_ENTITY_URL},
            ],
          },
        ],
      },
      {
        title: 'INSIGHTS',
        items: [
          checkAccess('withCarrierAppetite') && {
            icon: IconKey.CarrierAppetite,
            title: 'Carrier Appetite',
            to: DECISIONS_CARRIER_APPETITE,
            matchPattern: [{matchPattern: DECISIONS_CARRIER_APPETITE}],
          },
          checkAccess('withMarkets') && {
            icon: IconKey.Markets,
            title: 'Markets',
            to: DECISIONS_MARKETS,
            matchPattern: [
              {matchPattern: DECISIONS_MARKETS},
              {matchPattern: `${DECISIONS_MARKET_URL_BASE}/${MarketType.ICO}`},
              {matchPattern: `${DECISIONS_MARKET_URL_BASE}/${MarketType.BCO}`},
            ],
          },
          checkAccess('withIndustries') && {
            icon: IconKey.Industries,
            title: 'Industries',
            to: DECISIONS_INDUSTRIES,
            matchPattern: [
              {matchPattern: DECISIONS_INDUSTRY},
              {matchPattern: DECISIONS_INDUSTRY_GROUP},
              {matchPattern: DECISIONS_INDUSTRY_SUBGROUP},
              {matchPattern: DECISIONS_INDUSTRIES},
            ],
          },
          checkAccess('withProducts') && {
            icon: IconKey.Products,
            title: 'Products',
            to: DECISIONS_PRODUCTS,
            matchPattern: [
              {matchPattern: DECISIONS_PRODUCT},
              {matchPattern: DECISIONS_PRODUCT_GROUP},
              {matchPattern: DECISIONS_PRODUCT_SUPER_GROUP},
              {matchPattern: DECISIONS_PRODUCT_INSURANCE_SEGMENT},
              {matchPattern: DECISIONS_PRODUCTS},
            ],
          },
        ],
      },
      {
        title: 'TEAMS',
        items: [
          checkAccess('withAccountExecutives') && {
            icon: IconKey.AccountExecutives,
            title: 'Account Executives',
            to: DECISIONS_ACCOUNT_EXECUTIVES,
            matchPattern: [{matchPattern: DECISIONS_ACCOUNT_EXECUTIVES}, {matchPattern: DECISIONS_ACCOUNT_EXECUTIVE}],
          },
          checkAccess('withActivities') && {
            icon: IconKey.Activities,
            title: 'Activities',
            new: true,
            to: DECISIONS_ACTIVITIES,
            matchPattern: [
              {matchPattern: DECISIONS_ACTIVITIES},
              {matchPattern: DECISIONS_ACTIVITY_URL},
              {matchPattern: DECISIONS_EMPLOYEE_ACTIVITY_URL},
            ],
          },
          checkAccess('withAccountManagers') && {
            icon: IconKey.AccountManagers,
            title: 'Account Managers',
            to: DECISIONS_ACCOUNT_MANAGERS,
            matchPattern: [{matchPattern: DECISIONS_ACCOUNT_MANAGERS}, {matchPattern: DECISIONS_ACCOUNT_MANAGER}],
          },
          checkAccess('withProducers') && {
            icon: IconKey.Producers,
            title: 'Producers',
            to: DECISIONS_PRODUCERS,
            matchPattern: [{matchPattern: DECISIONS_PRODUCER}, {matchPattern: DECISIONS_PRODUCERS}],
          },
        ],
      },
    ] satisfies LoosySidebarSection[];
  }, [
    isMga,
    onlyDocumentsPermission,
    isProducer,
    checkAccess,
    newFeaturesCount,
    hasAlreadySeenWhatsNewPage,
    isProducerWithCarrierAppetite,
  ]);

  return useMemo(() => {
    const formattedSections = sections.map(section => ({
      title: section?.title,
      items: section?.items?.filter(Boolean),
    })) as SidebarSection[];

    return {
      home,
      sections: formattedSections.filter(section => Boolean(section.items.length)),
    };
  }, [home, sections]);
};
